import React, {useEffect, useMemo, useState} from 'react';
import {Link as RouterLink, useParams} from 'react-router-dom';
import moment from 'moment-timezone';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TradingChart from './TradingChart';
import api from '../services/api';

function getDecisionTone(decision) {
  switch (decision) {
    case 'BUY':
      return {bg: 'rgba(57, 217, 138, 0.12)', color: '#39D98A', border: 'rgba(57, 217, 138, 0.28)'};
    case 'SELL':
      return {bg: 'rgba(255, 107, 107, 0.12)', color: '#FF6B6B', border: 'rgba(255, 107, 107, 0.28)'};
    default:
      return {bg: 'rgba(245, 195, 107, 0.12)', color: '#F5C36B', border: 'rgba(245, 195, 107, 0.28)'};
  }
}

const statCardSx = {
  p: 3,
  borderRadius: 4,
  background: 'rgba(16, 29, 51, 0.82)',
  border: '1px solid rgba(126, 144, 168, 0.18)',
  height: '100%',
};

const AssetIntelligence = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const {symbol} = useParams();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const fetchAsset = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.get(`/assets/${symbol}/intelligence`);
        if (active) {
          setAsset(response.data);
        }
      } catch (fetchError) {
        console.error(fetchError);
        if (active) {
          setError(t('asset.loadError'));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchAsset();

    return () => {
      active = false;
    };
  }, [symbol, t]);

  const decisionTone = useMemo(
    () => getDecisionTone(asset?.decision),
    [asset?.decision],
  );

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 12}}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !asset) {
    return (
      <Container maxWidth="lg" sx={{py: 6}}>
        <Alert severity="error">{error || t('asset.unavailable')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{py: { xs: 3, md: 5 }}}>
      <Stack spacing={4}>
        <Paper
          sx={{
            p: {xs: 3, md: 5},
            borderRadius: 6,
            background:
              isDark
                ? 'linear-gradient(160deg, rgba(10,21,39,0.98) 0%, rgba(16,29,51,0.94) 55%, rgba(23,39,66,0.94) 100%)'
                : 'linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(243,248,253,0.96) 55%, rgba(236,243,250,0.98) 100%)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
          <Stack
            direction={{xs: 'column', lg: 'row'}}
            justifyContent="space-between"
            spacing={4}>
            <Stack spacing={2} maxWidth={740}>
              <Chip
                label={t('asset.eyebrow')}
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: 'rgba(88, 196, 221, 0.14)',
                  color: '#58C4DD',
                  border: '1px solid rgba(88, 196, 221, 0.25)',
                }}
              />
                <Typography variant="h3" sx={{fontWeight: 700, color: 'text.primary'}}>
                  {asset.symbol}
                </Typography>
              <Typography variant="h6" color="text.secondary" sx={{maxWidth: 640}}>
                {t('asset.subtitle')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                <Chip
                  label={asset.decision}
                  sx={{
                    backgroundColor: decisionTone.bg,
                    color: decisionTone.color,
                    border: `1px solid ${decisionTone.border}`,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  label={t('asset.confidenceChip', {
                    score: asset.confidence_score,
                    label: asset.confidence_label,
                  })}
                  sx={{
                    backgroundColor: 'rgba(88, 196, 221, 0.12)',
                    color: '#BEEAF4',
                    border: '1px solid rgba(88, 196, 221, 0.25)',
                  }}
                />
                <Chip
                  label={t('asset.sentimentChip', { label: asset.sentiment_label })}
                  sx={{
                    backgroundColor: 'rgba(245, 195, 107, 0.12)',
                    color: '#F5C36B',
                    border: '1px solid rgba(245, 195, 107, 0.25)',
                  }}
                />
              </Stack>
            </Stack>

            <Stack spacing={1.5} alignItems={{xs: 'flex-start', lg: 'flex-end'}}>
              <Button component={RouterLink} to="/dashboard" variant="outlined" color="inherit" sx={{ color: 'text.primary' }}>
                {t('asset.backToOverview')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('asset.lastDecision', {
                  value: asset.decision_timestamp
                    ? moment(asset.decision_timestamp).locale(i18n.language === 'gr' ? 'el' : 'en').fromNow()
                    : t('common.noRecentDecision'),
                })}
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper sx={statCardSx}>
              <Typography variant="overline" color="text.secondary">
                {t('asset.price')}
              </Typography>
              <Typography variant="h4" sx={{fontWeight: 700, mt: 1, color: 'text.primary'}}>
                ${asset.price?.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: asset.price_change_24h >= 0 ? '#39D98A' : '#FF6B6B',
                }}>
                {asset.price_change_24h >= 0 ? '+' : ''}
                {t('asset.priceChange24h', {
                  value: `${asset.price_change_24h >= 0 ? '+' : ''}${asset.price_change_24h}`,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={statCardSx}>
              <Typography variant="overline" color="text.secondary">
                {t('asset.decisionStreak')}
              </Typography>
              <Typography variant="h4" sx={{fontWeight: 700, mt: 1, color: 'text.primary'}}>
                {asset.prediction_streak || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                {t('asset.decisionStreakHelp')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={statCardSx}>
              <Typography variant="overline" color="text.secondary">
                {t('asset.supportDistance')}
              </Typography>
              <Typography variant="h4" sx={{fontWeight: 700, mt: 1, color: 'text.primary'}}>
                {asset.support_distance_pct ?? '--'}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                {t('asset.supportDistanceHelp')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={statCardSx}>
              <Typography variant="overline" color="text.secondary">
                {t('asset.resistanceDistance')}
              </Typography>
              <Typography variant="h4" sx={{fontWeight: 700, mt: 1, color: 'text.primary'}}>
                {asset.resistance_distance_pct ?? '--'}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                {t('asset.resistanceDistanceHelp')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{...statCardSx, p: 3.5}}>
              <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                {t('asset.priceStructure')}
              </Typography>
              <TradingChart
                data={asset.chart || []}
                support={asset.support_levels || []}
                resistance={asset.resistance_levels || []}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper sx={{...statCardSx, p: 3.5}}>
              <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                {t('asset.whyThisView')}
              </Typography>
              <Stack spacing={1.5}>
                {asset.explanations?.map((item, index) => (
                  <Paper
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: isDark ? 'rgba(88, 196, 221, 0.06)' : 'rgba(17, 127, 164, 0.06)',
                      border: '1px solid',
                      borderColor: isDark ? 'rgba(88, 196, 221, 0.14)' : 'rgba(17, 127, 164, 0.12)',
                    }}>
                    <Typography variant="body2" color="text.secondary">
                      {item}
                    </Typography>
                  </Paper>
                ))}
              </Stack>

              <Divider sx={{my: 3, borderColor: 'rgba(126, 144, 168, 0.14)'}} />

              <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                {t('asset.keyLevels')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{mb: 2}} useFlexGap>
                {(asset.support_levels || []).map(level => (
                  <Chip key={`support-${level}`} label={t('asset.supportLevel', { level })} />
                ))}
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                {(asset.resistance_levels || []).map(level => (
                  <Chip key={`resistance-${level}`} label={t('asset.resistanceLevel', { level })} />
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{...statCardSx, p: 3.5}}>
          <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
            {t('asset.recentTrail')}
          </Typography>
          <List disablePadding>
            {(asset.recent_predictions || []).map((item, index) => (
              <React.Fragment key={`${item.timestamp}-${index}`}>
                <ListItem disableGutters sx={{py: 1.5}}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip
                          label={item.prediction}
                          sx={{
                            backgroundColor: getDecisionTone(item.prediction).bg,
                            color: getDecisionTone(item.prediction).color,
                            border: `1px solid ${getDecisionTone(item.prediction).border}`,
                          }}
                        />
                        <Typography variant="body1" sx={{ color: 'text.primary' }}>
                          {moment(item.timestamp).locale(i18n.language === 'gr' ? 'el' : 'en').format('DD MMM, HH:mm')}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
                {index < asset.recent_predictions.length - 1 ? (
                  <Divider sx={{borderColor: 'rgba(126, 144, 168, 0.12)'}} />
                ) : null}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Stack>
    </Container>
  );
};

export default AssetIntelligence;
